import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TableContainer,
  Paper,
  CircularProgress,
  Button as MuiButton,
} from "@mui/material";
import styled from "styled-components";
import CloseIcon from "@mui/icons-material/Close";
import { Add, Refresh } from "@mui/icons-material";
import api from "../../api/config";
import TableWithPagination from "./TableWithPagination";
import AddClientLocationModal from "./AddClientLocationModal";
import { GetClientLocation } from "../../api/Patient";
import { DeleteClientLocation } from "../../api/Client";

const Header = styled(DialogTitle)`
  background-color: #28929c;
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px;
  font-size: 14px;
`;

const Content = styled(DialogContent)`
  background-color: #2b3342;
  color: white;
  padding: 16px;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-top: 16px;
  max-height: 500px;
  background-color: #2b3342;
`;

const DialogActionsStyled = styled(DialogActions)`
  justify-content: space-between;
  padding: 16px;
  background: #28929c;
`;

const StyledPaper = styled(Paper)`
  background-color: #2b3342 !important;
  color: #fbfeff !important;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const Button = styled.button`
  background-color: #3b4252;
  color: #fbfeff;
  border: none;
  border-radius: 100px;
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  max-height: 40px;
  margin-bottom: 15px;
  align-items: center;
  gap: 8px;

  &:hover {
    background-color: #4c566a;
  }
`;

const ClientLocationModal = ({ open, onClose, location, locations = [] }) => {
  const [loading, setLoading] = useState(true);
  const [locationsList, setLocationsList] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

  const columns = [
    { id: "uuid", label: "ID", display: "hidden" },
    { id: "enpoint_name", label: "Location Name" },
    { id: "npi_name", label: "NPI Name" },
    { id: "npi_number", label: "NPI Number" },
    {
      id: "actions",
      label: "Actions",
      buttons: [
        { label: "Edit", id: "edit" },
        { label: "Delete", id: "delete" },
      ],
    },
  ];

  const handleClose = () => {
    setLocationsList([]);
    onClose();
  };

  const handleRefresh = () => {
    getClientLocation();
  };

  useEffect(() => {
    if (open) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && locations && locations.length > 0) {
      setLoading(false);
      getClientLocation();
    }
  }, [open, locations]);

  const handleEdit = (location) => {
    setSelectedClient(location);
    setIsModalOpen(true);
  };

  const getClientLocation = async () => {
    setLoading(true);
    try {
      const response = await api.get(
        `${GetClientLocation}?ClientUID=${location.uuid}`
      );
      console.log("Client location response:", response);
      setLocationsList(response.data);
    } catch (error) {
      console.error("Error fetching client location:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = (action, row) => {
    if (action === "edit") {
      console.log("Edit Client Location1:", row);
      handleEdit(row);
      console.log("Edit Client Location:", row);
    } else if (action === "delete") {
      setSelectedClient(row);
      setOpenDeleteDialog(true);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      const response = await api.post(
        `${DeleteClientLocation}/${selectedClient.uuid}`
      );

      console.log("Client location deleted successfully", response);
      setOpenDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting location:", error);
    } finally {
      setLoading(false);
      getClientLocation();
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: { width: "80vw", maxHeight: "80vh" },
        }}
      >
        <Header>
          Show location for: {location?.npi_name}
          <IconButton onClick={handleClose} style={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Header>
        <Content>
          <StyledTableContainer component={StyledPaper}>
            {loading ? (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px",
                  backgroundColor: "#2B3342",
                }}
              >
                <CircularProgress style={{ color: "#FBFEFF" }} />
              </div>
            ) : (
              <>
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <Button
                    onClick={() => {
                      setSelectedClient(null);
                      setIsModalOpen(true);
                    }}
                  >
                    <Add />
                    New Location
                  </Button>
                </div>
                <TableWithPagination
                  title="Locations"
                  columns={columns}
                  rows={locationsList}
                  noSearch
                  height="450px"
                  totalRecords={locationsList.length}
                  page={0}
                  limit={locationsList.length}
                  isLoading={false}
                  onActionClick={handleActionClick}
                  onRefresh={handleRefresh}
                />
              </>
            )}
          </StyledTableContainer>
        </Content>
        <DialogActionsStyled>
          <Button color="secondary" variant="contained" onClick={handleClose}>
            Close
          </Button>
        </DialogActionsStyled>
      </Dialog>

      <AddClientLocationModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        selectedClient={selectedClient}
        selectedLocation={location}
        isEdit={!!selectedClient}
        onSaveSuccess={() => {
          setIsModalOpen(false);
          setSelectedClient(null);
          getClientLocation();
        }}
      />

      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        PaperProps={{
          sx: { width: "80vw", maxHeight: "80vh" },
        }}
      >
        <Content>
          <StyledTableContainer>
            Are you sure you want to delete this location?
          </StyledTableContainer>
        </Content>

        <DialogActionsStyled>
          <MuiButton onClick={() => setOpenDeleteDialog(false)} color="primary">
            Cancel
          </MuiButton>
          <MuiButton
            onClick={handleDelete}
            color="secondary"
            variant="contained"
          >
            Yes, Delete
          </MuiButton>
        </DialogActionsStyled>
      </Dialog>
    </>
  );
};

export default ClientLocationModal;
